/**
 * SKUs recognized as base product (Portal Essentials and variants).
 * Centralizes the list to avoid hardcoded checks scattered around (DAF-448).
 *
 * Add new variants here (e.g. DAF-ESS+<X>-001) when the Salesforce catalog
 * introduces an equivalent new base product.
 */
export const BASE_PRODUCT_SKUS: ReadonlySet<string> = new Set([
  'DAF-ESS-001',
  'DAF-ESS+TS-001',
])

export const isBaseProductSku = (sku?: string | null): boolean =>
  !!sku && BASE_PRODUCT_SKUS.has(sku)

/** Legacy DAF Connect. No longer sold; the 60-day window offers Portal Essentials monthly instead. */
export const DAF_CONNECT_SKU = 'DAF-CONN-001'

/** Truck Sales 10-year pack. Inside the 60-day window it extends as Portal Essentials monthly (DAF-711). */
export const PORTAL_ESSENTIALS_TS_SKU = 'DAF-ESS+TS-001'

export const PORTAL_ESSENTIALS_SKU = 'DAF-ESS-001'

/**
 * Products that, with fewer than 60 days left, extend as Portal Essentials monthly (DAF-ESS-001).
 * DAF Connect is legacy and must not be sold again. The TS 10-year pack follows the same path.
 */
const SIXTY_DAY_EXTEND_SKUS: ReadonlySet<string> = new Set([
  DAF_CONNECT_SKU,
  PORTAL_ESSENTIALS_TS_SKU,
])

type ActiveServiceLike = {
  sku?: string | null
  Sku?: string | null
  effectiveEndDate?: string | null
}

export const canExtendAsMonthlyEssentials = (
  sku: string | null | undefined,
  daysToExpire: unknown,
  activeServices: ActiveServiceLike[] = [],
  windowDays = 60
): boolean => {
  const days = Number(daysToExpire)
  if (!sku || !SIXTY_DAY_EXTEND_SKUS.has(sku) || !Number.isFinite(days) || days >= windowDays) {
    return false
  }

  // Connect is already covered when any Essentials variant is active, so that card stays hidden.
  // The TS pack is the product being extended; its own row must not block the button.
  if (sku !== DAF_CONNECT_SKU) {
    return true
  }

  const connect = activeServices.find(service => (service.sku ?? service.Sku) === DAF_CONNECT_SKU)
  const essentials = activeServices.find(service => isBaseProductSku(service.sku ?? service.Sku))
  if (connect?.effectiveEndDate && essentials?.effectiveEndDate) {
    return false
  }

  return true
}
